import { createNativeStackNavigator } from "@react-navigation/native-stack";
import {createStackNavigator} from "@react-navigation/stack";
import { FlatList, Platform, Pressable, StyleSheet, Modal, TextInput, ScrollView } from "react-native";
import { View, Text, Title } from "@constants/Themed";
import { DataStackParamList } from "../types";
import React, { useMemo, useState, useCallback } from "react";
import CaptureScreen from "../screens/MidLevel/DataScreen/CaptureScreen/CaptureScreen";
import UploadScreen from "../screens/MidLevel/DataScreen/UploadScreen";
import CreateDataScreen from "../screens/MidLevel/DataScreen/CreateDataScreen";
import DataBrowserScreen from "../screens/LowLevel/DataBrowserScreen";
import TimeLineScreen from "../screens/LowLevel/TimeLineScreen";
import InvoiceDetailScreen from "../screens/LowLevel/InvoiceDetailScreen";
import DataHandler from '../screens/MidLevel/DataScreen/DataHandler';
import PublicDataScreen from "../screens/MidLevel/DataScreen/PublicDataScreen/PublicDataScreen";
import EmailTemplates from "../screens/MidLevel/DataScreen/TemplatesScreen/EmailTemplates";
import InvoiceTemplates from "../screens/MidLevel/DataScreen/TemplatesScreen/InvoiceTemplates";
import DataMenu from "../screens/MidLevel/DataScreen/DataMenu";
import DataTemplate from "../screens/MidLevel/DataScreen/DataTemplates";
import WhatsappTemplates from "../screens/MidLevel/DataScreen/WhatsappTemplates";
import CreateEmailTemplate from "../screens/LowLevel/CreateEmailTemplateScreen";
import NewCreativeTemplate from "@navigation/screens/MidLevel/DataScreen/TemplatesScreen/CreativeTemplates/NewCreativeTemplate";
import DocumentTemplate from "../screens/MidLevel/DataScreen/TemplatesScreen/DocumentTemplate";
import DocumentTemplateEditor from "@navigation/screens/MidLevel/DataScreen/TemplatesScreen/DocumentTemplates/DocumentTemplateEditor";
import IndividualPublicDataScreen from "../screens/MidLevel/DataScreen/PublicDataScreen/IndividualPublicDataScreen";
import DataFavouritesScreen from "../screens/MidLevel/DataScreen/DataFavourites/DataFavouritesScreen";
import ProductScreen from "../screens/LowLevel/ProductScreen";
import GenericDataItemScreen from "../screens/LowLevel/GenericDataItemScreen";
import InvoiceScreen from "../screens/MidLevel/DataScreen/TemplatesScreen/InvoiceTemplates/InvoiceScreen";
import ActionHandlerScreen from "../screens/MidLevel/DataScreen/ActionHandler/ActionHandlerScreen";
import Utilities from "../screens/MidLevel/DataScreen/Utilities";
import Button from "../../components/UI/Button/Button";
import toTitleCase from "@hooks/toTitleCase";
import parseByDelimiter from "@utils/parseByDelimiter";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import ActionArray from "@components/UI/ContextActions.tsx/ActionArray";
import { CLEAR_ACTION_HANDLER_HISTORY, SET_DATAFILTER } from "@reduxLocal/actions/types";
import apis, { getDataFavorites } from "@api/api";
import { useQuery } from "@tanstack/react-query";
import { useNavigation } from "@react-navigation/native";
import { AntDesign, FontAwesome, FontAwesome5 } from "@expo/vector-icons";
import { useDispatch, useSelector } from "react-redux";
import DataHandlerHeader from "./Components/DataHandlerHeader";
import PreviewEmailTemplate from "../screens/MidLevel/DataScreen/TemplatesScreen/PreviewEmailTemplate";
import { Step, StepLabel, Stepper } from '@mui/material';
import { DefaultRootState } from "@reduxLocal/persistState";
import CreativeTemplates from "@navigation/screens/MidLevel/DataScreen/TemplatesScreen/CreativeTemplates";
import stages from "./constants/stages";
import * as Animatable from 'react-native-animatable';
import Feather from '@expo/vector-icons/Feather';
import { LinearGradient } from "expo-linear-gradient";
import { CountUp } from 'use-count-up';
import { SizableText, XStack, YStack, H6 } from "tamagui";
import styles from "@stylesheet";
import { noMonitor } from "@api/api";
import { SHOW_AUTH_MODAL, SET_ACTIONS } from "@reduxLocal/actions/types";
import { FlowNavigator, FlowStack} from "./FlowStack";
import { useDeviceType } from '@hooks/useDeviceType';
import { isTablet } from "react-native-device-info";
import MediaScreen from "@navigation/screens/MidLevel/DataScreen/MediaScreen";
import DataBubbleScreen from "@navigation/screens/MidLevel/DataScreen/DataBubble/DataBubbleScreen";
import ComponentDesigner from '@navigation/NavigationStacks/Components/ComponentDesigner';

export const filters = [
  {
    value: 'all',
    icon: <FontAwesome5 name="cloud-meatball" size={24} style={{ maxWidth: 30, top: 5 }} />,
    description: "Showing you all the data you have access to."
  },
  {
    value: 'products',
    icon: <FontAwesome name="shopping-basket" size={24} style={{ maxWidth: 30, top: 5 }}  />,
    description: "Showing you all the products you have access to."
  },
  {
    value: 'customers',
    icon: <Feather name="users" size={24} style={{ maxWidth: 30, top: 5 }} />,
    description: "Showing you all the customers you have access to."
  },
  {
    value: 'inventory',
    icon: <FontAwesome5 name="boxes" size={24} style={{ maxWidth: 30, top: 5 }} />,
    description: "Showing you all the inventory items you have access to."
  }
]

const DataStack = Platform.select({default: createNativeStackNavigator<DataStackParamList>(), web: createStackNavigator<DataStackParamList>()});

const DataHeader = () => {
  const dispatch = useDispatch();
  const theme = themeSelector();
  const dataFilter = useSelector((state: DefaultRootState) => state.UI.dataFilter); // Get current filter from Redux
  const navigation = useNavigation();
  const tablet = isTablet();

  const { status: favStatus, data: favouriteData, error: favError, isFetching: favFetching, isLoading: favLoading, refetch: refetchFav } = useQuery({
    queryKey: ['favouriteData'],
    queryFn: async () => {
      const data = await getDataFavorites()
        .then(res => {
          return res
        });
      return data
    },
    enabled: Platform.OS ==='web'
  })

  return useMemo(()=> (
    <View style={{ width: "100%", alignSelf: "stretch", paddingHorizontal: "5%", height: Platform.select({ web: undefined, default: 60 }), maxHeight: 40, flexDirection: 'row', borderBottomStartRadius: 20, gap: 10, borderBottomEndRadius: 20, justifyContent: 'space-between', alignItems: 'center', padding: 20, paddingTop: 35, top: tablet && 30, }}>
      <View style={{ minWidth: "100%", flex: 1, flexDirection: "row", top: Platform.OS === 'web' ? 0 : (tablet ? -5 : -4), minHeight: 40, maxWidth: "100%" }}>
        <ActionArray conjoined forceRow smallButton defaultIndex={0} wrap={false} withShadow actionArray={[
          {
            functionName: "all", function: () => { dispatch({ type: SET_DATAFILTER, payload: "all" }); }, icon: <FontAwesome5 name="cloud-meatball" size={Platform.select({web:24, native: 14})} style={{ maxWidth: 30 }} color={dataFilter === "all" ? "black" : (theme ? "black" : "white")} />, text: "All"
          },
          {
            functionName: "customers", function: () => { dispatch({ type: SET_DATAFILTER, payload: "customers" }); }, icon: <Feather name="users" size={Platform.select({web:24, native: 14})} style={{ maxWidth: 30 }} color={dataFilter === "customers" ? "black" : (theme ? "black" : "white")} />, text: "Customers"
          },
          {
            functionName: "products", function: () => { dispatch({ type: SET_DATAFILTER, payload: "products" }); }, icon: <FontAwesome name="shopping-basket" size={Platform.select({web:24, native: 14})} style={{ maxWidth: 30 }} color={dataFilter === "products" ? "black" : (theme ? "black" : "white")} />, text: "Products"
          },
          {
            functionName: "inventory", function: () => { dispatch({ type: SET_DATAFILTER, payload: "inventory" }); }, icon: <FontAwesome5 name="boxes" size={Platform.select({web:24, native: 14})} style={{ maxWidth: 30 }} color={dataFilter === "inventory" ? "black" : (theme ? "black" : "white")} />, text: "Inventory"
          },
          {
            functionName: "documents",
            function: () => { dispatch({ type: SET_DATAFILTER, payload: "documents" }); },
            icon: <FontAwesome5 name="file-alt" size={Platform.select({ web: 24, native: 14 })} style={{ maxWidth: 30 }} color={dataFilter === "documents" ? "black" : (theme ? "black" : "white")} />,
            text: "Documents"
          },
          {
            functionName: "invoices",
            function: () => { dispatch({ type: SET_DATAFILTER, payload: "invoices" }); },
            icon: <FontAwesome5 name="file-invoice" size={Platform.select({ web: 24, native: 14 })} style={{ maxWidth: 30, top: 2 }} color={dataFilter === "invoices" ? "black" : (theme ? "black" : "white")} />,
            text: "Invoices"
          }
        ]} activeComponent={dataFilter} disableExpand forceMinimized />
      </View>
    </View>
  ), [theme, tablet, dataFilter, favouriteData])
}

// AI Flow Suggestion Modal Component
const AIFlowSuggestionModal = ({ visible, onClose, dataId, dataTitle, currentActions }) => {
  const theme = themeSelector();
  const dispatch = useDispatch();
  const [description, setDescription] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleGetSuggestions = useCallback(async () => {
    if (!description || description.trim().length < 10) {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Please enter at least 10 characters', type: 'warning' }
      });
      return;
    }

    setLoading(true);
    try {
      const response = await noMonitor.post('/ai/flow/suggest-automation', {
        description,
        dataSchemaId: dataId
      });

      if (response.data.success && response.data.flows) {
        setSuggestions(response.data.flows);
      }
    } catch (error) {
      console.error('Failed to get AI flow suggestions:', error);
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Failed to get AI suggestions', type: 'error' }
      });
    }
    setLoading(false);
  }, [description, dataId, dispatch]);

  const handleApplyFlow = useCallback((flow) => {
    // Convert AI flow stages to action format
    const actions = flow.stages.map(stage => ({
      key: stage.key,
      icon: stage.icon,
      title: stage.title,
      actionKey: stage.actionKey,
      type: stage.type,
      config: stage.config || {}
    }));

    // Dispatch actions to Redux
    dispatch({ type: SET_ACTIONS, payload: actions });

    dispatch({
      type: SHOW_AUTH_MODAL,
      data: { content: `Applied: ${flow.name}`, type: 'success' }
    });

    onClose();
    setSuggestions([]);
    setDescription('');
  }, [dispatch, onClose]);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={{
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20
      }} onPress={onClose}>
        <Pressable style={{
          backgroundColor: theme ? '#ffffff' : '#1e293b',
          borderRadius: 20,
          padding: 24,
          width: Platform.OS === 'web' ? 600 : '90%',
          maxHeight: '80%',
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.3,
          shadowRadius: 20
        }} onPress={(e) => e.stopPropagation()}>

          <H6 style={{ marginBottom: 16, color: 'rgb(90, 192, 234)' }}>
            🤖 AI Flow Assistant
          </H6>

          <Text style={{ marginBottom: 12, fontSize: 14 }}>
            Describe what you want to automate for: {dataTitle}
          </Text>

          <TextInput
            placeholder="e.g., Send welcome email when customer is added"
            value={description}
            onChangeText={setDescription}
            multiline
            style={{
              backgroundColor: theme ? '#f3f4f6' : '#374151',
              borderRadius: 12,
              padding: 12,
              marginBottom: 16,
              minHeight: 80,
              textAlignVertical: 'top',
              color: theme ? '#000' : '#fff'
            }}
          />

          <Pressable
            onPress={handleGetSuggestions}
            disabled={loading}
            style={{
              backgroundColor: loading ? '#ccc' : 'rgb(90, 192, 234)',
              borderRadius: 12,
              padding: 14,
              alignItems: 'center',
              marginBottom: 16
            }}
          >
            <Text style={{ color: 'white', fontWeight: '600' }}>
              {loading ? 'Getting Suggestions...' : 'Get AI Suggestions'}
            </Text>
          </Pressable>

          <ScrollView style={{ maxHeight: 400 }}>
            {suggestions.length > 0 && (
              <View style={{ gap: 12 }}>
                <Text style={{ fontWeight: '600', marginBottom: 8 }}>
                  Suggested Flows:
                </Text>
                {suggestions.map((flow, index) => (
                  <Pressable
                    key={index}
                    onPress={() => handleApplyFlow(flow)}
                    style={{
                      backgroundColor: 'rgba(90, 192, 234, 0.1)',
                      borderRadius: 12,
                      padding: 12,
                      borderWidth: 1,
                      borderColor: 'rgba(90, 192, 234, 0.3)'
                    }}
                  >
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                      <Text style={{ fontWeight: '600', flex: 1 }}>
                        {flow.name}
                      </Text>
                      <Text style={{ fontSize: 12, color: '#666' }}>
                        {((flow.confidence || 0.5) * 100).toFixed(0)}% match
                      </Text>
                    </View>

                    <Text style={{ fontSize: 13, marginBottom: 8, color: '#666' }}>
                      {flow.description}
                    </Text>

                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                      {flow.actions && flow.actions.map((action, i) => (
                        <View key={i} style={{
                          backgroundColor: 'rgb(90, 192, 234)',
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                          borderRadius: 6
                        }}>
                          <Text style={{ color: 'white', fontSize: 11 }}>
                            {action}
                          </Text>
                        </View>
                      ))}
                    </View>

                    {flow.requires_setup && flow.requires_setup.length > 0 && (
                      <Text style={{ fontSize: 11, color: 'orange', marginTop: 6 }}>
                        ⚠️ Requires: {flow.requires_setup.join(', ')}
                      </Text>
                    )}

                    {flow.fallback && (
                      <Text style={{ fontSize: 10, color: '#999', marginTop: 4 }}>
                        (Basic suggestion - AI service unavailable)
                      </Text>
                    )}
                  </Pressable>
                ))}
              </View>
            )}
          </ScrollView>

          <Pressable
            onPress={onClose}
            style={{
              marginTop: 16,
              padding: 12,
              alignItems: 'center',
              borderRadius: 12,
              backgroundColor: theme ? '#f3f4f6' : '#374151'
            }}
          >
            <Text style={{ color: theme ? '#000' : '#fff' }}>Close</Text>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const DataNavigator = () => {
  const theme = themeSelector();
  return (
    <DataStack.Navigator
      screenOptions={{ headerShown: false, animation: 'fade', cardStyle: { backgroundColor: theme ? "white" : "black" } }}
      initialRouteName={'Data'}
    >
      <DataStack.Screen
        name="DataMenu"
        options={{
          animation: 'fade',
          gestureEnabled: false,
          headerShown: true,
          header(props) {
            return Platform.OS === 'web' && (
              <DataHeader />
            )
          },
          title: "Data Menu" // Add title here
        }}
        component={Platform.OS === 'web' ? CaptureScreen : DataMenu} />
        <DataStack.Screen
        name="DocumentTemplateEditor"
        component={DocumentTemplateEditor}
        options={{
          animation: 'fade',
          freezeOnBlur: true,
          title: "Document Template Editor" // Add title here
        }}
      />
      <DataStack.Screen
        name="DataStore"
        component={CaptureScreen}
        options={{
          animation: 'fade',
          freezeOnBlur: true,
          gestureEnabled: false,
          headerShown: true,
          header(props) {
            return (
              <DataHeader />)
          },
          title: "Data Store" // Add title here
        }}
      />
      <DataStack.Screen
        name="Utilities"
        component={Utilities}
        options={{ freezeOnBlur: true }}
      />
      <DataStack.Screen
        name="Media"
        component={MediaScreen}
        options={{ freezeOnBlur: true }}
      />
      <DataStack.Screen
        name="Upload"
        component={UploadScreen}
        options={{
          headerShown: Platform.OS === 'web',
          header(props) {
            const [tutorial, setTutorial] = useState(stages[0]);
            const uploaderStage = useSelector((state: DefaultRootState) => state.UI.uploaderStage);
            return (
              <View style={{ paddingHorizontal: 30, marginLeft: Platform.OS === 'web' ? 30 : undefined, maxHeight: 80, marginRight: Platform.OS === 'web' ? 30 : undefined, shadowRadius: 10, backgroundColor: theme ? 'white' : '#1e293b', shadowOpacity: 0.2, flex: 1, alignSelf: "stretch", flexDirection: 'row', borderBottomStartRadius: 20, gap: 10, borderBottomEndRadius: 20, justifyContent: 'space-between', alignItems: 'center', padding: 20, borderBottomColor: 'lightgrey' }}>
                <Title style={{ flex: 1, textAlign: "left" }}>Upload your data</Title>
                <View style={{ flex: 3 }}>
                  <Stepper activeStep={uploaderStage} alternativeLabel>
                    {
                      stages.map((stage, index) => {
                        return (
                          <Step key={index}>
                            <StepLabel color={theme ? "black" : "white"}>{stage.title}</StepLabel>
                          </Step>
                        )
                      })
                    }
                  </Stepper>
                </View>
              </View>
            )
          },
          title: "Upload" // Add title here
        }}
      />
      <DataStack.Screen
        name="ActionHandler"
        options={{
          animation: 'fade',
          gestureEnabled: false,
          headerShown: false,
          title: "Action Handler"
        }}
        component={ActionHandlerScreen}
      />
      <DataStack.Group>
        <DataStack.Screen
          name="DataTemplates"
          component={DataTemplate}
          options={{ headerShown: false, title: "Data Templates" }} // Add title here
        />
        <DataStack.Screen
          name="CreativeTemplates"
          component={CreativeTemplates}
          options={{ headerShown: false, title: "Creative Templates" }} // Add title here
        />
        <DataStack.Screen
          name="NewCreativeTemplate"
          component={NewCreativeTemplate}
          options={{ title: "New Creative Template" }} // Add title here
        />
        <DataStack.Screen
          name="EmailTemplates"
          options={{ title: "Email Templates" }}
          component={EmailTemplates} />
        <DataStack.Screen
          name="InvoiceTemplates"
          component={InvoiceTemplates}
          options={{ title: "Invoice Templates" }} // Add title here
        />
        <DataStack.Screen
        name="BubbleScreen"
        component={DataBubbleScreen}
        />
        <FlowStack.Screen
          name="FlowStack"
          component={FlowNavigator}
        />
        <DataStack.Screen
          name='Invoice'
          component={InvoiceScreen}
          options={{ title: "Invoice" }} // Add title here
        />
        <DataStack.Screen
          name="DocumentTemplates"
          component={DocumentTemplate}
          options={{ headerShown: false, title: "Document Templates" }} // Add title here
        />
        <DataStack.Screen
          name="CreateEmailTemplate"
          component={CreateEmailTemplate}
          options={{ headerShown: false, title: "Create Email Template" }} // Add title here
        />
        <DataStack.Screen
          name="PreviewEmailTemplate"
          component={PreviewEmailTemplate}
          options={{ headerShown: false, title: "Preview Email Template" }} // Add title here
        />
        <DataStack.Screen
          name="WhatsappTemplates"
          component={WhatsappTemplates}
          options={{ headerShown: false, title: "Whatsapp Templates" }} // Add title here
        />
      </DataStack.Group>
      <DataStack.Screen
        name="DataFavourites"
        component={DataFavouritesScreen}
        options={{ title: "Data Favourites" }} // Add title here
      />
      <DataStack.Screen
        name="CreateData"
        component={CreateDataScreen}
        options={{
          title: "Create Data",
          headerShown: true,
          header(props) {
            const selected = useSelector((state: DefaultRootState) => state.UI.selectedData);
            return selected && (
              <Animatable.View useNativeDriver style={{ backgroundColor: "rgb(90,192,234)", minHeight: 60, borderBottomStartRadius: 20, borderBottomEndRadius: 20 }} animation={'slideInDown'} >
                <Text>{selected.dataTitle || "TEST"}</Text>
              </Animatable.View>
            )
          },
        }} // Add title here
      />
      <DataStack.Screen
        name='TimeLineData'
        component={TimeLineScreen}
        options={{ title: "Timeline Data" }} // Add title here
      />
      <DataStack.Screen
        name='InvoiceDetail'
        component={InvoiceDetailScreen}
        options={{ title: "Invoice Details", headerShown: false }} // Add title here
      />
      <DataStack.Screen
        name='ProductScreen'
        component={ProductScreen}
        options={{ title: "Product Screen" }} // Add title here
      />
      <DataStack.Screen
        name='GenericDataItemScreen'
        component={GenericDataItemScreen}
        options={{ title: "Data Item" }} // Add title here
      />
      <DataStack.Screen
        name='DataBrowser'
        component={DataBrowserScreen}
        options={{
          title: "Data Browser",
        }}
      />
      <DataStack.Screen
        name='ComponentDesigner'
        component={ComponentDesigner}
        options={{
          title: "Data Browser",
        }}
      />
      <DataStack.Screen
        name='DataHandler'
        component={DataHandler}
        options={{
          animation: 'slide_from_right', headerShown: true, gestureEnabled: false, header() {
          return (
            <DataHandlerHeader />
          )
          }, title: "Data Handler"
        }} // Add title here
      />
      <DataStack.Screen
        name='PublicData'
        component={PublicDataScreen}
        options={{ title: "Public Data" }} // Add title here
      />
      <DataStack.Screen
        name='IndividualPublicData'
        component={IndividualPublicDataScreen}
        options={{ title: "Individual Public Data" }} // Add title here
      />
    </DataStack.Navigator>
  );
}

const localStyles = StyleSheet.create({
  actionHandlerHeader: { shadowRadius: 20, shadowOpacity: .2, marginLeft: Platform.OS === 'web' ? 30 : undefined, flexDirection: Platform.select({ web: 'row', default: "column" }), gap: 10, justifyContent: 'space-between', alignItems: 'center', padding: 10, maxWidth: Platform.select({ web: "100%", default: "100%" }), borderBottomColor: 'lightgrey' }
})

export { DataStack, DataNavigator }